import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import type { Event, Graph, WithContext } from 'schema-dts'
import type { EventDescription } from '~/composables/types/event'

const useEventSchema = (
  events: EventDescription[],
  overwrites: any = undefined
): any => {
  const schema = ref<Graph[] | undefined>(undefined)
  const script = computed(() => ({
    type: 'application/ld+json',
    innerHTML: schema.value,
  }))

  const eventAttendanceModeOptions = {
    HYBRID: 'MixedEventAttendanceMode',
    IN_PERSON: 'OfflineEventAttendanceMode',
    ONLINE: 'OnlineEventAttendanceMode',
  }

  const fullLocalizedUrl = useFullLocalizedUrl()
  const {
    public: { baseUrl },
  } = useRuntimeConfig()

  const createSchema = (): Event[] => {
    function formatStartDate(startDate: string | null, hasDates: boolean) {
      dayjs.extend(utc)
      dayjs.extend(timezone)

      return hasDates && startDate
        ? dayjs(startDate).tz('Europe/Berlin').toISOString()
        : dayjs().tz('Europe/Berlin').add(1, 'month').toISOString()
    }

    return events.map((event): WithContext<Event> => {
      const hasDates = Boolean(eventGetters.getNextDateAvailable(event))
      const url = fullLocalizedUrl(
        `/${
          eventGetters.isGiftcardEvent(event) ? 'giftcard' : 'e'
        }/${eventGetters.getPermalink(event)}/`
      )

      const reviewSchemaArr = eventGetters
        .getReviewsList(event)
        .map((review) => {
          return {
            '@type': 'Review',
            reviewRating: {
              '@type': 'Rating',
              ratingValue: reviewGetters.getReviewRating(review),
            },
            reviewBody: reviewGetters.getReview(review),
            author: {
              '@type': 'Person',
              name: reviewGetters.getAuthor(review),
            },
          }
        })

      const obj: WithContext<Event> = {
        '@type': 'Event',
        name: eventGetters.getTitle(event),
        url,
        startDate: formatStartDate(
          eventGetters.getNextDateAvailable(event),
          hasDates
        ),
        endDate: formatStartDate(
          eventGetters.getNextDateAvailableEnd(event),
          hasDates
        ),
        eventAttendanceMode:
          eventAttendanceModeOptions[eventGetters.getAttendanceMode(event)],
        // TODO dynamic eventStatus
        eventStatus: 'https://schema.org/EventScheduled',
        image: eventGetters.getGallery(event).map((x) => x.cropped),
        description: eventGetters.getSubtitle(event),
        offers: {
          '@type': 'Offer',
          availability: 'InStock',
          price: eventGetters.getDefaultPriceAmount(event) / 100,
          priceCurrency: eventGetters.getDefaultPriceCurrency(event),
          priceSpecification: eventGetters.getSchemaPriceSpecification(event),
          url,
        },
        performer: {
          '@type': 'PerformingGroup',
          name: eventGetters.getSupplierName(event),
        },
        organizer: {
          '@type': 'Organization',
          name: eventGetters.getSupplierName(event),
          url: fullLocalizedUrl(
            supplierGetters.getKonfettiProfilePath(
              eventGetters.getSupplier(event)
            )
          ),
        },
        // location: null,
        ...(eventGetters.getReviewsTotal(event) &&
          eventGetters.getReviewsTotal(event) > 0 && {
            review: reviewSchemaArr,
            aggregateRating: {
              '@type': 'AggregateRating',
              ratingValue: eventGetters.getReviewsAverage(event, 'en-US'),
              bestRating: 5,
              worstRating: 1,
              ratingCount: eventGetters.getReviewsTotal(event),
            },
          }),
      }

      if (eventGetters.getAttendanceMode(event) !== 'ONLINE') {
        obj.location = {
          '@type': 'Place',
          name:
            overwrites?.locality?.name ||
            eventGetters.getAddressLocality(event).name,
          address: {
            '@type': 'PostalAddress',
            name: eventGetters.getFormattedAddress(event),
            postalCode: eventGetters.getAddressZipcode(event),
          },
        }
      } else {
        obj.location = {
          '@type': 'VirtualLocation',
          url: baseUrl,
        }
      }

      return obj
    })
  }

  schema.value = { '@context': 'https://schema.org', '@graph': createSchema() }

  return {
    schema,
    script,
  }
}

export default useEventSchema
